import React from "react";
import { usePowerDialer } from "@/context/PowerDialerContext";
import type { CampaignContactStatus } from "@/model/interface/CampaignContact";
import { getContactDisplayName } from "@/util/string";
import { cn } from "@/lib/utils";

const LINE_STATUS_LABELS: Partial<Record<CampaignContactStatus, string>> = {
  DIALING: "Dialing",
  RINGING: "Ringing",
  ANSWERED: "Answered",
  CONNECTED: "Live",
  ON_HOLD: "On hold",
};

const BRIDGED_LINE_STATUSES: CampaignContactStatus[] = ["CONNECTED", "ON_HOLD"];

export function LineSlotStrip() {
  const { lineCalls, lines, isPreview, activeCall } = usePowerDialer();
  const slotCount = Math.max(isPreview ? 1 : lines, lineCalls.length);
  const orderedCalls = [...lineCalls].sort(
    (first, second) =>
      Number(BRIDGED_LINE_STATUSES.includes(second.status)) - Number(BRIDGED_LINE_STATUSES.includes(first.status)),
  );

  if (slotCount <= 1) {
    return null;
  }

  return (
    <div className="flex gap-2">
      {Array.from({ length: slotCount }, (_, index) => {
        const call = orderedCalls[index];
        const isLive = call !== undefined && call.id === activeCall?.id;
        const dotColor = call ? (isLive ? "var(--success)" : "var(--warning)") : undefined;
        const name = call ? (getContactDisplayName(call.contact) ?? call.phoneNumber) : null;
        return (
          <div
            key={call?.id ?? `idle-line-${index}`}
            className={cn(
              "flex min-w-0 flex-1 items-center gap-2 rounded-md border border-border px-3 py-2 text-sm",
              isLive && "bg-muted font-semibold",
            )}
          >
            <span
              className={cn("h-2 w-2 shrink-0 rounded-full", !call && "bg-muted-foreground/30", call && !isLive && "animate-pulse")}
              style={dotColor ? { backgroundColor: dotColor } : undefined}
            />
            <span className="shrink-0">Line {index + 1}</span>
            <span className="truncate text-muted-foreground">
              {call ? `${LINE_STATUS_LABELS[call.status] ?? call.status} · ${name}` : "Idle"}
            </span>
          </div>
        );
      })}
    </div>
  );
}
